import { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { JSONTree } from "react-json-tree";
import type { SourceConfig } from "@tuit/shared-types";
import styles from "../../styles/components/AdminPage.module.css";

interface Props {
  config: SourceConfig | null;
  sourceId: string;
  loading?: boolean;
  onSaved?: (updated: SourceConfig) => void;
}

function SourceConfigView({ config, onEdit }: { config: SourceConfig; onEdit: () => void }) {
  return (
    <div>
      <table className={styles.table}>
        <tbody>
          <tr><td className={styles.configKey}>ID</td><td>{config.id}</td></tr>
          <tr><td className={styles.configKey}>Title</td><td>{config.title}</td></tr>
          <tr>
            <td className={styles.configKey}>URL</td>
            <td>
              <a href={config.url} target="_blank" rel="noreferrer" className={styles.itemLink}>
                {config.url}
              </a>
            </td>
          </tr>
          <tr><td className={styles.configKey}>Thumbnails</td><td>{config.thumbnails ? "Yes" : "No"}</td></tr>
          <tr><td className={styles.configKey}>Refresh</td><td>{config.refreshIntervalMinutes} min</td></tr>
        </tbody>
      </table>
      <div className={styles.editBtnRow}>
        <button type="button" className={styles.actionBtn} onClick={onEdit}>Edit</button>
      </div>
    </div>
  );
}

function SourceConfigEditForm({
  config,
  onSaved,
  onCancel,
}: {
  config: SourceConfig;
  onSaved: (updated: SourceConfig) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(config.title);
  const [url, setUrl] = useState(config.url);
  const [thumbnails, setThumbnails] = useState(config.thumbnails);
  const [refreshIntervalMinutes, setRefreshIntervalMinutes] = useState(config.refreshIntervalMinutes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const res = await fetch(`/api/source/${config.id}/config`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, url, thumbnails, refreshIntervalMinutes }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      const updated = await res.json() as SourceConfig;
      onSaved(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={styles.editForm}>
      <div className={styles.editRow}>
        <label htmlFor="sc-title" className={styles.editLabel}>Title</label>
        <input id="sc-title" className={styles.editInput} value={title} onChange={(e) => setTitle(e.target.value)} required />
      </div>
      <div className={styles.editRow}>
        <label htmlFor="sc-url" className={styles.editLabel}>RSS URL</label>
        <input id="sc-url" className={styles.editInput} value={url} onChange={(e) => setUrl(e.target.value)} required type="url" />
      </div>
      <div className={styles.editRow}>
        <label htmlFor="sc-refresh" className={styles.editLabel}>Refresh (min)</label>
        <input
          id="sc-refresh"
          className={styles.editInput}
          type="number"
          min={1}
          value={refreshIntervalMinutes}
          onChange={(e) => setRefreshIntervalMinutes(Number(e.target.value))}
          required
        />
      </div>
      <div className={styles.editRow}>
        <label htmlFor="sc-thumbnails" className={styles.editLabel}>Thumbnails</label>
        <input id="sc-thumbnails" type="checkbox" checked={thumbnails} onChange={(e) => setThumbnails(e.target.checked)} />
      </div>
      {error && <div className={styles.opsResultError}>{error}</div>}
      <div className={styles.editActions}>
        <button type="submit" className={styles.actionBtn} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" className={styles.actionBtnSecondary} onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function SourceConfigTable({ config: initialConfig, sourceId, loading, onSaved }: Props) {
  const [view, setView] = useState<"ux" | "json">("ux");
  const [editing, setEditing] = useState(false);
  const [config, setConfig] = useState(initialConfig);

  useEffect(() => {
    setConfig(initialConfig);
    setEditing(false);
  }, [initialConfig]);

  function handleSaved(updated: SourceConfig) {
    setConfig(updated);
    setEditing(false);
    onSaved?.(updated);
  }

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>
          Source Config:{" "}
          {sourceId ? (
            <Link to="/source/$sourceId" params={{ sourceId }} className={styles.panelTitleLink}>
              {sourceId}
            </Link>
          ) : null}
        </h2>
        {config && !editing && (
          <div className={styles.viewToggle}>
            {(["ux", "json"] as const).map((v) => (
              <button
                key={v}
                type="button"
                className={`${styles.viewToggleBtn} ${view === v ? styles.viewToggleBtnActive : ""}`}
                onClick={() => setView(v)}
              >
                {v.toUpperCase()}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className={styles.panelBody}>
        {!config
          ? <span className={styles.panelEmpty}>{loading ? "Loading…" : "Load a Source Config to see details."}</span>
          : editing
            ? <SourceConfigEditForm config={config} onSaved={handleSaved} onCancel={() => setEditing(false)} />
            : view === "json"
              ? <JSONTree data={config} />
              : <SourceConfigView config={config} onEdit={() => setEditing(true)} />
        }
      </div>
    </div>
  );
}
